/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useState } from 'react'
import { Box, Grid, Link } from '@mui/material'
import { getThemeColor, PlatformEvent } from 'lib/util'
import { useMemoRef, useStyles } from 'platform/react/hook'
import OfflineHOC from 'platform/react/hoc/offline'
import SkeletonHOC from 'platform/react/hoc/skeleton'
import Text from 'ui/Element/Text'

const styles = (theme, { backgroundImage, backgroundPosition, onClick }) => ({
  root: {
    position: 'relative',
    height: '11rem', // 176px
    padding: '1.5rem',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    textDecoration: 'none',
    backgroundColor: getThemeColor(theme, 'gray.960'),
    '&:hover': {
      backgroundColor: getThemeColor(theme, 'gray.930'),
      ...onClick && { cursor: 'pointer' },
    },
  },
  background: {
    position: 'absolute',
    ...backgroundPosition === 'right' ? {
      top: '-3.75rem',
      right: '-1.875rem',
      width: '10.5rem',
      height: '10.5rem',
      filter: 'grayscale(100%) opacity(0.5)',
    } : {
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    background: `url(/assets/${backgroundImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  ...theme.custom.card,
})

/**
 * Card Element
 *
 * Can also fetch data using the `events.onOpen` handler. There are 3 possible scenarios:
 *
 * - 1: We don't have an onOpen handler. In this case we simply show the card without any delay.
 * - 2: We have an onOpen handler, but it doesn't return data. In this case we wait for the handler
 *   to finish, but don't do anything with its (non-existent) result and show the card.
 * - 3: We have an onOpen handler, and it returns useful data: In this case we set `data` to
 *   the incoming data, then show the card. We also pass the data to the onClick handler. It will
 *   pass it to the new action and let it do its thing.
 *
 * In any case, if there is an onOpen handler, we will display a skeleton until it returns.
 *
 * @param {Object} p
 * @param {React.Ref} p.forwardedRef        ref to the root element
 * @param {Gaia.Component.Events} p.events  events for the component
 * @param {string} p.label                  label
 * @param {string} p.subLabel               sub label
 * @param {string} [p.backgroundImage]      optional background image
 * @param {boolean} [p.link=false]          whether the component should act as a link
 * @param {string} [p.target='_blank']      optional target for the link
 * @param {string} p.color.label            color for the label
 * @param {string} p.color.subLabel         color for the sub label
 * @param {string} p.textVariant.label      variant for the label
 * @param {string} p.textVariant.subLabel   variant for the sub label
 * @returns {JSX.Element}
 */
const Card = ({
  forwardedRef,
  events,
  label,
  subLabel,
  backgroundImage,
  backgroundPosition,

  link = false,
  target = '_blank',

  color: {
    label: labelColor = 'gray.contrastText',
    subLabel: subLabelColor = 'gray.400',
  } = {},
  textVariant: {
    label: labelVariant = '20/medium',
    subLabel: subLabelVariant = '14/regular',
  } = {},
}) => {
  const { onClick, onOpen } = events
  const [data, setData] = useState(null)

  const classes = useStyles(styles, { backgroundImage, backgroundPosition, onClick })()

  useEffect(() => {
    (async () => {
      const result = await onOpen?.(null)

      result && setData(result)
    })()
  }, [])

  useEffect(() => {
    (data !== null || !onOpen) && events?.showSkeleton?.(false)
  }, [data])

  const handleClick = useCallback(e => onClick?.(new PlatformEvent(e, { data })), [onClick, data])

  return (
    <Grid
      container
      ref={forwardedRef}

      {...onClick && {
        component: Box,
        onClick: handleClick,
      }}

      {...link && data && {
        component: Link,
        target,
        href: data,
      }}

      direction={'column'}
      className={classes.root}
      justifyContent={'space-between'}
    >
      {backgroundImage && <div className={classes.background} />}
      <Grid
        item
        component={Text}

        maxLines={1}
        value={label}
        variant={labelVariant}
        color={labelColor}
      />
      <Grid
        item
        component={Text}

        maxLines={2}
        value={subLabel}
        color={subLabelColor}
        variant={subLabelVariant}
      />
    </Grid>
  )
}

export default useMemoRef(OfflineHOC(SkeletonHOC(Card)), [])
